import React, { useEffect, useState } from "react";

import {
  Box,
  Card,
  Heading,
  Hidden,
  Stack,
  StackSection,
  Text,
} from "@origin-digital/ods-core";
import { useAppContext } from "../../context/AppContext";
import { Offers } from "../../components/Offers/Offers";
import {
  NavigationURIType,
  submitNavigationEvent,
} from "../../utils/analytics";
import { AddressSelector } from "../../components/AddressSelector/AddressSelector";
import { AddressEntryType, IConnectionAddress } from "../../types";
import { originAddressReverseLookup } from "../../services/addressLookup";
import { useLocation } from "react-router-dom";
import { QasProfile } from "@origin-digital/address-lookup";
import {
  mapCIDOrCampaignToOrgID,
  setAttribution,
  setCID,
} from "@origin-digital/sales-common";
import content from "./content.json";
import { config } from "../../config";

const OFFER_CODES_WITHOUT_EDR = new Set([
  "lpgbundleoffer",
  "lpgmoveoffer",
  "movers50",
  "fmmovers40",
  "mamovers40",
  "welcomeoffer",
]);

export const OffersScreen: React.FC = () => {
  const { context, setContext } = useAppContext();
  const { search } = useLocation();
  const [addressSelectorError, setAddressSelectorError] = React.useState<
    string | undefined
  >();
  const [hasNaturalGas, setHasNaturalGas] = useState<boolean>(false);
  const [naturalGasOriginAddressId, setNaturalGasOriginAddressId] = useState<
    string | undefined
  >();
  const offers = context?.offersData?.offers ?? [];
  const deliverability = context?.offersData?.deliverability;
  const locationType = context?.offersData?.locationValidation?.locationType;
  const loading = context.loading || false;
  const [navEventFired, setNavEventFired] = useState<boolean>(false);
  const offerCode = new URLSearchParams(search).get("offercode")?.toLowerCase();
  const shouldHideRewardsBanner =
    !!offerCode && OFFER_CODES_WITHOUT_EDR.has(offerCode);

  if (!navEventFired) {
    const {
      location: { href },
    } = window;
    submitNavigationEvent(href, NavigationURIType.OFFERS);
    setNavEventFired(true);
  }

  const fetchExternalOriginAddress = async (
    externalAddressID: string,
    addressType: AddressEntryType = AddressEntryType.EXTERNAL_ADDRESS
  ): Promise<IConnectionAddress | undefined> => {
    const addressObj = await originAddressReverseLookup(externalAddressID);
    return addressObj
      ? {
          ...addressObj,
          addressType,
        }
      : undefined;
  };

  /**
   * When a pre-loaded ConnectionAddress is available fill the
   * address component with that. Also, pass that connection address
   * to `onChange` callback to load plans
   */
  useEffect(() => {
    const query = new URLSearchParams(search);
    const cidParam = query.get("cid");

    if (!context.lookupAddress) {
      const originAddressId = query.get("originAddressId");
      if (originAddressId && originAddressId !== "undefined") {
        fetchExternalOriginAddress(originAddressId).then((address) => {
          context.lookupAddress = address;
          setContext(context);
        });
      }
    }

    if (cidParam) {
      const affiliateId = mapCIDOrCampaignToOrgID(
        cidParam,
        (window as any).oetal.config["lpgplansassets/lpgplans"].env
      );
      setAttribution({ affiliateID: affiliateId });
      setCID(cidParam);
    }
  }, []);

  const addressSelectorSection = (
    <AddressSelector
      data-id="addressLookup"
      connectionAddress={context.lookupAddress}
      label="Your address"
      allowManualAddressEntry={true}
      qasProfile={QasProfile.AUG}
      errorMessage={addressSelectorError}
      setErrorMessage={setAddressSelectorError}
      onNaturalGasDetected={(originAddressId) => {
        setHasNaturalGas(true);
        setNaturalGasOriginAddressId(originAddressId);
      }}
      onAddressReset={() => {
        setHasNaturalGas(false);
        setNaturalGasOriginAddressId(undefined);
      }}
    />
  );

  const rewardsBannerSection = (
    <Card
      data-id="rewardsBanner"
      padding="medium"
      blur="small"
      borderRadius="large"
    >
      <Box
        display="flex"
        flexDirection={{ xs: "column", lg: "row" }}
        gap={{ xs: "medium", lg: "xlarge" }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap="medium"
          justifyContent="space-between"
        >
          <Hidden above="lg">
            <Heading component="h2" variant="h3">
              {content.rewardsBanner.heading}
            </Heading>
          </Hidden>
          <Box display="flex">
            <img
              src={content.rewardsBanner.logoUrl}
              width="96"
              alt={content.rewardsBanner.logoAlt}
            />
          </Box>
        </Box>
        <Box display="flex" flexDirection="column" width="full" gap="small">
          <Hidden below="lg">
            <Heading component="h2" variant="h3">
              {content.rewardsBanner.heading}
            </Heading>
          </Hidden>
          <Text>{content.rewardsBanner.description}</Text>
        </Box>
      </Box>
    </Card>
  );

  const offersSection = (
    <Offers
      offers={offers}
      deliverability={deliverability}
      locationType={locationType}
      loading={loading}
      setAddressSelectorError={setAddressSelectorError}
      hasNaturalGas={hasNaturalGas}
      naturalGasOriginAddressId={naturalGasOriginAddressId}
    />
  );

  return (
    <Card
      borderRadius="none"
      paddingXSpecific="none"
      paddingYSpecific="none"
      backgroundColor="transparent"
    >
      <Box>
        <StackSection
          width="large"
          hideGutter={{ xs: true, sm: false }}
          paddingY="large"
        >
          <Box>
            <Stack space="xlarge">
              <Box>
                <Card padding="medium" blur="small" borderRadius="large">
                  <Stack space="xlarge">{addressSelectorSection}</Stack>
                </Card>
              </Box>
              {config.showEdrBanner &&
                !context.lookupAddress &&
                !shouldHideRewardsBanner && <Box>{rewardsBannerSection}</Box>}
            </Stack>
          </Box>
          <Box>{offersSection}</Box>
        </StackSection>
      </Box>
    </Card>
  );
};
